import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Bike, Gift, Loader2, AlertCircle } from 'lucide-react';
import api from '../lib/api';
import PageHeader from '../components/PageHeader';
import { useCart } from '../context/CartContext';
import { usePrepTime } from '../hooks/usePrepTime';
import { CONTACT } from '../mock';

const MAX_POLLS = 30;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const nav = useNavigate();
  const { clear } = useCart();
  const { prep } = usePrepTime();
  const [state, setState] = useState('checking'); // checking | paid | failed
  const [summary, setSummary] = useState(null);
  const clearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState('failed'); return; }
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === 'paid') {
          setSummary(data.order || {});
          setState('paid');
          if (!clearedRef.current) {
            clearedRef.current = true;
            clear();
            localStorage.removeItem('wrappus_ref');
            localStorage.removeItem('wrappus_pending_order');
          }
          return;
        }
        if (['failed', 'expired'].includes(data.payment_status)) { setState('failed'); return; }
      } catch (_) { /* keep polling */ }
      if (tries >= MAX_POLLS) { setState('failed'); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <main>
      <PageHeader title={state === 'paid' ? 'Order Confirmed' : 'Confirming Payment'} breadcrumb="Confirmation" />
      <section className="py-20 bg-white">
        <div className="max-w-2xl mx-auto px-6 text-center">
          {state === 'checking' && (
            <div data-testid="payment-checking">
              <Loader2 size={40} className="mx-auto mb-5 animate-spin text-[#ED2E2E]" />
              <h1 className="section-title text-2xl md:text-4xl mb-3">Confirming your payment…</h1>
              <p className="text-gray-600">Hang tight, this usually takes a couple of seconds. Please don't close this page.</p>
            </div>
          )}

          {state === 'failed' && (
            <div data-testid="payment-failed">
              <div className="w-20 h-20 mx-auto rounded-full bg-red-100 text-red-600 flex items-center justify-center mb-6">
                <AlertCircle size={40} />
              </div>
              <h1 className="section-title text-2xl md:text-4xl mb-3">We couldn't confirm your payment</h1>
              <p className="text-gray-600 mb-6">No charge is kept if the payment didn't complete. You can try again — your cart is still saved.</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button onClick={() => nav('/checkout')} className="btn-wrappus text-sm" data-testid="payment-failed-retry">Back to Checkout</button>
                <a href={`tel:${CONTACT.phoneRaw}`} className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white border-2 border-[#ED2E2E] text-[#ED2E2E] font-semibold hover:bg-[#FDF3EC] transition">Call {CONTACT.phone}</a>
              </div>
            </div>
          )}

          {state === 'paid' && (
            <div data-testid="payment-success">
              <div className="w-20 h-20 mx-auto rounded-full bg-green-100 text-green-600 flex items-center justify-center mb-6">
                <CheckCircle2 size={40} />
              </div>
              <h1 className="section-title text-3xl md:text-5xl mb-4">Thank You!</h1>
              <p className="text-gray-600 mb-2">Your order has been placed and payment received.</p>
              <p className="text-gray-500 mb-6 text-sm">Order ID: <span className="font-mono" data-testid="success-order-id">{summary?.orderId}</span></p>

              {summary?.pointsEarned > 0 && (
                <div className="bg-gradient-to-r from-[#ED2E2E]/10 to-[#F5A623]/10 border border-[#ED2E2E]/20 rounded-2xl p-5 mb-6">
                  <div className="flex items-center gap-2 justify-center text-[#ED2E2E] font-bold text-lg"><Gift size={20} /> +{summary.pointsEarned} loyalty points earned!</div>
                  {summary.signupBonus > 0 && <p className="text-[#0F1C2E] text-sm mt-2">Welcome to Wrappus! We threw in <strong>+{summary.signupBonus} bonus points</strong> for signing up.</p>}
                  {summary.referralBonusAwarded && <p className="text-[#0F1C2E] text-sm mt-1">Your friend's referral bonus has been credited to their account.</p>}
                  <p className="text-gray-700 text-sm mt-1">Reach 100 points for a $10 reward on your next order.</p>
                </div>
              )}

              {summary?.trackingUrl ? (
                <div className="bg-[#FDF3EC] rounded-2xl p-6 mb-6">
                  <div className="flex items-center gap-2 justify-center mb-2 text-[#ED2E2E] font-bold"><Bike size={18} /> Your delivery is on the way</div>
                  <p className="text-gray-700 text-sm mb-3">Track your Uber Direct delivery in real time:</p>
                  <a href={summary.trackingUrl} target="_blank" rel="noopener noreferrer" className="btn-wrappus text-sm">Track Delivery</a>
                </div>
              ) : (
                <p className="text-gray-700 mb-8">Pickup at <strong>{CONTACT.address}</strong>. {prep?.rushActive
                  ? prep.message + '.'
                  : `We'll have it ready in ~${prep?.etaMinutes ?? 10} minutes.`} Questions? Call <a href={`tel:${CONTACT.phoneRaw}`} className="text-[#ED2E2E] font-semibold">{CONTACT.phone}</a>.</p>
              )}

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a href={`/track/${summary?.orderId}`} className="btn-wrappus text-sm" data-testid="success-track-btn">Track Order Status</a>
                <button onClick={() => nav('/menu')} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border-2 border-[#ED2E2E] text-[#ED2E2E] font-semibold hover:bg-[#FDF3EC] transition">Order More</button>
              </div>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
